import React, { useState, useEffect } from 'react';
import './Absences.css';
import { useNavigate, useParams } from 'react-router-dom';
import { absencesAPI } from '../services/api';
import toast from 'react-hot-toast';
import { ChevronLeft, Calendar } from 'lucide-react';

const TYPE_OPTIONS = [
  { value: 'CONGE_PAYE',       label: 'Congé payé' },
  { value: 'RTT',              label: 'RTT' },
  { value: 'CONGE_SANS_SOLDE', label: 'Congé sans solde' },
];

const fmtNum = (n) => n % 1 === 0 ? `${n}` : `${n}`;

export default function AbsenceForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const year = new Date().getFullYear();

  const [balance, setBalance]         = useState(null);
  const [existingReq, setExistingReq] = useState(null);
  const [form, setForm]               = useState({
    type_conge:  'CONGE_PAYE',
    date_debut:  '',
    date_fin:    '',
    depart_midi: false,
    retour_midi: false,
    description: '',
  });
  const [nbJours, setNbJours]         = useState(null);
  const [saving, setSaving]           = useState(false);
  const [simulating, setSimulating]   = useState(false);
  const [loading, setLoading]         = useState(isEdit);

  useEffect(() => {
    absencesAPI.balance(year)
      .then(r => setBalance(r.data))
      .catch(() => {});
  }, [year]);

  useEffect(() => {
    if (!isEdit) return;
    absencesAPI.list({})
      .then(r => {
        const req = (r.data || []).find(x => x.id === id);
        if (req) {
          setExistingReq(req);
          setForm({
            type_conge:  req.type_conge,
            date_debut:  req.date_debut,
            date_fin:    req.date_fin,
            depart_midi: req.depart_midi,
            retour_midi: req.retour_midi,
            description: req.description || '',
          });
          setNbJours(req.nb_jours);
        }
      })
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  const set = (field) => (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm(f => ({ ...f, [field]: val }));
  };

  // Re-simulate when dates or demi-journées change
  useEffect(() => {
    if (!form.date_debut || !form.date_fin) { setNbJours(null); return; }
    if (form.date_fin < form.date_debut) { setNbJours(null); return; }
    setSimulating(true);
    absencesAPI.simulate({
      type_conge:  form.type_conge,
      date_debut:  form.date_debut,
      date_fin:    form.date_fin,
      depart_midi: form.depart_midi,
      retour_midi: form.retour_midi,
    })
      .then(r => setNbJours(r.data.nb_jours))
      .catch(() => setNbJours(null))
      .finally(() => setSimulating(false));
  }, [form.date_debut, form.date_fin, form.depart_midi, form.retour_midi]);

  const validate = () => {
    if (!form.date_debut) { toast.error('Sélectionnez une date de début'); return false; }
    if (!form.date_fin)   { toast.error('Sélectionnez une date de fin');   return false; }
    if (form.date_fin < form.date_debut) { toast.error('La date de fin doit être après la date de début'); return false; }
    if (!nbJours || nbJours <= 0) { toast.error('La période ne contient aucun jour ouvré'); return false; }
    return true;
  };

  const handleSave = async (andSubmit = false) => {
    if (!validate()) return;
    setSaving(true);
    try {
      let reqId;
      if (isEdit && existingReq) {
        // Re-create: cancel old + create new (brouillon can't be updated via PUT in API, simplest approach)
        await absencesAPI.cancel(existingReq.id);
        const res = await absencesAPI.create({
          type_conge:  form.type_conge,
          date_debut:  form.date_debut,
          date_fin:    form.date_fin,
          depart_midi: form.depart_midi,
          retour_midi: form.retour_midi,
          description: form.description || null,
        });
        reqId = res.data.id;
      } else {
        const res = await absencesAPI.create({
          type_conge:  form.type_conge,
          date_debut:  form.date_debut,
          date_fin:    form.date_fin,
          depart_midi: form.depart_midi,
          retour_midi: form.retour_midi,
          description: form.description || null,
        });
        reqId = res.data.id;
      }
      if (andSubmit) {
        await absencesAPI.submit(reqId);
        toast.success('Demande soumise pour validation');
      } else {
        toast.success('Demande enregistrée');
      }
      navigate('/absences');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="loading-state"><div className="spinner"/></div>;

  const readOnly = isEdit && existingReq && existingReq.statut !== 'BROUILLON';
  const cp  = balance?.cp;
  const rtt = balance?.rtt;

  return (
    <div>
      <div className="page-header">
        <div className="page-header-left">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button className="btn btn-ghost btn-sm" onClick={() => navigate('/absences')}>
              <ChevronLeft size={16}/>
            </button>
            <div>
              <h1 style={{ margin: 0 }}>{isEdit ? 'Modifier la demande' : 'Nouvelle demande de congé'}</h1>
            </div>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, alignItems: 'start' }}>

        {/* Récapitulatif solde */}
        {balance && (
          <div className="card">
            <h3 style={{ margin: '0 0 16px', fontSize: '0.9rem', fontWeight: 600 }}>
              Récapitulatif de vos congés {year}
            </h3>
            <table className="abs-recap-table">
              <thead>
                <tr>
                  <th></th>
                  <th>Congés payés</th>
                  <th>RTT</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Acquis</td>
                  <td>{cp?.acquis}</td>
                  <td>{rtt?.acquis}</td>
                </tr>
                <tr>
                  <td>Pris</td>
                  <td style={{ color: 'var(--text-muted)' }}>{cp?.pris}</td>
                  <td style={{ color: 'var(--text-muted)' }}>{rtt?.pris}</td>
                </tr>
                <tr>
                  <td>Restants</td>
                  <td style={{ fontWeight: 600, color: 'var(--primary)' }}>{cp?.restants}</td>
                  <td style={{ fontWeight: 600, color: '#8b5cf6' }}>{rtt?.restants}</td>
                </tr>
                <tr>
                  <td>Demandés</td>
                  <td style={{ color: '#f59e0b' }}>{cp?.soumis}</td>
                  <td style={{ color: '#f59e0b' }}>{rtt?.soumis}</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 600 }}>Jours demandés courant</td>
                  <td style={{ fontWeight: 700, color: form.type_conge === 'CONGE_PAYE' ? 'var(--primary)' : 'var(--text-muted)' }}>
                    {form.type_conge === 'CONGE_PAYE' && nbJours != null ? fmtNum(nbJours) : '0'}
                  </td>
                  <td style={{ fontWeight: 700, color: form.type_conge === 'RTT' ? '#8b5cf6' : 'var(--text-muted)' }}>
                    {form.type_conge === 'RTT' && nbJours != null ? fmtNum(nbJours) : '0'}
                  </td>
                </tr>
                <tr style={{ borderTop: '2px solid var(--border)' }}>
                  <td style={{ fontWeight: 600 }}>Restants prévus</td>
                  <td style={{ fontWeight: 700, color: 'var(--primary)' }}>
                    {cp ? fmtNum(
                      form.type_conge === 'CONGE_PAYE' && nbJours
                        ? Math.max(0, cp.prevus - nbJours)
                        : cp.prevus
                    ) : '—'}
                  </td>
                  <td style={{ fontWeight: 700, color: '#8b5cf6' }}>
                    {rtt ? fmtNum(
                      form.type_conge === 'RTT' && nbJours
                        ? Math.max(0, rtt.prevus - nbJours)
                        : rtt.prevus
                    ) : '—'}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* Formulaire */}
        <div className="card">
          <h3 style={{ margin: '0 0 16px', fontSize: '0.9rem', fontWeight: 600 }}>
            <Calendar size={15} style={{ verticalAlign: 'middle', marginRight: 6 }}/>
            Ajouter le congé
          </h3>

          <div className="form-group">
            <label className="form-label">Type de congé *</label>
            <select className="form-control" value={form.type_conge} onChange={set('type_conge')}>
              {TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Dates *</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={{ fontSize: '0.75rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>
                  Début
                </label>
                <input
                  type="date"
                  className="form-control"
                  value={form.date_debut}
                  onChange={set('date_debut')}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
              <div>
                <label style={{ fontSize: '0.75rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>
                  Fin
                </label>
                <input
                  type="date"
                  className="form-control"
                  value={form.date_fin}
                  onChange={set('date_fin')}
                  min={form.date_debut || new Date().toISOString().split('T')[0]}
                />
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 24, margin: '8px 0 16px' }}>
            <label className="abs-checkbox">
              <input type="checkbox" checked={form.depart_midi} onChange={set('depart_midi')}/>
              <span>Départ à 12h <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>(demi-journée)</span></span>
            </label>
            <label className="abs-checkbox">
              <input type="checkbox" checked={form.retour_midi} onChange={set('retour_midi')}/>
              <span>Retour à 14h <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>(demi-journée)</span></span>
            </label>
          </div>

          {/* Nb jours calculé */}
          {(nbJours != null || simulating) && (
            <div className="abs-nb-jours">
              {simulating ? (
                <span style={{ color: 'var(--text-muted)' }}>Calcul en cours…</span>
              ) : (
                <>
                  <Calendar size={15}/>
                  <strong>{fmtNum(nbJours)} jour{nbJours > 1 ? 's' : ''} ouvré{nbJours > 1 ? 's' : ''}</strong>
                  <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                    (week-ends et jours fériés exclus)
                  </span>
                </>
              )}
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Description <span style={{ color: 'var(--text-muted)' }}>(optionnel)</span></label>
            <textarea
              className="form-control"
              rows={3}
              value={form.description}
              onChange={set('description')}
              placeholder="Motif, précisions…"
              style={{ resize: 'vertical' }}
            />
          </div>

          <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
            <button
              className="btn btn-secondary"
              onClick={() => navigate('/absences')}
              disabled={saving}
            >
              Annuler
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => handleSave(false)}
              disabled={saving || !nbJours}
            >
              {saving ? '…' : 'Enregistrer sans soumettre'}
            </button>
            <button
              className="btn btn-primary"
              onClick={() => handleSave(true)}
              disabled={saving || !nbJours}
            >
              {saving ? '…' : 'Enregistrer et soumettre'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
